
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/auth-context";
import { WalletData } from "@/models/wallet";
import { Purchase } from "@/models/purchase";
import { User } from "@/models/user";
import { WalletService } from "@/services/wallet-service";
import { PurchaseService } from "@/services/purchase-service";
import { toast } from "@/components/ui/use-toast";
import { useLocation } from "react-router-dom";

export interface UseDashboardReturnType {
  user: User | null;
  loading: boolean;
  walletData: WalletData | null;
  loadingWallet: boolean;
  purchases: Purchase[];
  loadingPurchases: boolean;
  refreshWallet: () => Promise<void>;
  refreshPurchases: () => Promise<void>;
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

export const useDashboard = (): UseDashboardReturnType => {
  const { user } = useAuth();
  const location = useLocation();
  const [walletData, setWalletData] = useState<WalletData | null>(null);
  const [loadingWallet, setLoadingWallet] = useState<boolean>(true);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loadingPurchases, setLoadingPurchases] = useState<boolean>(true);
  
  // Determine initial tab from URL query parameters or set defaults based on user type
  const getInitialTab = () => {
    const queryParams = new URLSearchParams(location.search);
    const tabFromQuery = queryParams.get('tab');
    
    if (tabFromQuery) {
      return tabFromQuery;
    }
    
    return user?.profileType === 'creator' ? "overview" : "overview";
  };

  const [activeTab, setActiveTab] = useState<string>(getInitialTab());

  // Update URL when tab changes
  const handleSetActiveTab = (tab: string) => {
    setActiveTab(tab);
    
    // Update the URL with the new tab
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('tab', tab);
    window.history.pushState({}, '', newUrl.toString());
  };

  const fetchWallet = async () => {
    if (!user) {
      setWalletData(null);
      setLoadingWallet(false);
      return;
    }

    setLoadingWallet(true);
    try {
      const wallet = await WalletService.getWallet(user.id);
      setWalletData(wallet);
    } catch (error) {
      console.error("Error fetching wallet:", error);
      toast({
        title: "Error",
        description: "Failed to load wallet data. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoadingWallet(false);
    }
  };

  const fetchPurchases = async () => {
    if (!user) {
      setPurchases([]);
      setLoadingPurchases(false);
      return;
    }

    setLoadingPurchases(true);
    try {
      const userAgents = await PurchaseService.getPurchasedAgents(user.id);
      
      // Convert UserAgentRow to Purchase model
      const purchaseData: Purchase[] = userAgents.map(row => ({
        id: row.id,
        userId: row.user_id,
        agentId: row.agent_id,
        agentName: row.agent_id, // Using agent_id as placeholder since agent_name doesn't exist
        agentDescription: "", // Empty string since agent_description doesn't exist
        amount: row.amount,
        purchaseDate: new Date(row.purchase_date),
        transactionId: row.transaction_id
      }));
      
      setPurchases(purchaseData);
    } catch (error) {
      console.error("Error fetching purchases:", error);
      toast({
        title: "Error",
        description: "Failed to load purchase data. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoadingPurchases(false);
    }
  };

  const refreshWallet = async () => {
    await fetchWallet();
  };

  const refreshPurchases = async () => {
    await fetchPurchases();
  };

  useEffect(() => {
    // Check for tab in URL when component mounts or location changes
    setActiveTab(getInitialTab());
  }, [location.search]);

  useEffect(() => {
    if (user) {
      fetchWallet();
      fetchPurchases();
    }
  }, [user]);

  return {
    user,
    loading: !user,
    walletData,
    loadingWallet,
    purchases,
    loadingPurchases,
    refreshWallet,
    refreshPurchases,
    activeTab,
    setActiveTab: handleSetActiveTab
  };
};
